import { UserStory } from '../types/story';

const HU_ID_PATTERN = /^HU-(\d+)$/i;
const HU_NUMBER_PATTERN = /HU-?(\d+)/i;

export const buildHuId = (sequence: number): string => {
  const safe = Number.isFinite(sequence) && sequence > 0 ? Math.round(sequence) : 1;
  return `HU-${String(safe).padStart(3, '0')}`;
};

export const normalizeStoryId = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = String(value).trim().toUpperCase();
  if (!trimmed) return null;

  const exactMatch = trimmed.match(HU_ID_PATTERN);
  if (exactMatch) {
    return buildHuId(Number(exactMatch[1]));
  }

  const huMatch = trimmed.match(HU_NUMBER_PATTERN);
  if (huMatch) {
    return buildHuId(Number(huMatch[1]));
  }

  if (/^\d+$/.test(trimmed)) {
    return buildHuId(Number(trimmed));
  }

  return trimmed;
};

export const getStoryIdForDisplay = (story: Pick<UserStory, 'id'>, fallbackIndex: number): string => {
  const normalized = normalizeStoryId(story.id);
  if (normalized && /^HU-\d+$/i.test(normalized)) {
    return normalized;
  }
  return buildHuId(fallbackIndex + 1);
};

export const getNextStoryId = (stories: Array<Pick<UserStory, 'id'>>): string => {
  let max = 0;
  stories.forEach((story) => {
    const normalized = normalizeStoryId(story.id);
    if (!normalized) return;
    const match = normalized.match(HU_ID_PATTERN);
    if (!match) return;
    max = Math.max(max, Number(match[1]));
  });
  return buildHuId(max + 1);
};

export const ensureUniqueStoryIds = (stories: UserStory[]): UserStory[] => {
  const used = new Set<string>();
  let sequence = 1;

  return stories.map((story) => {
    let candidate = normalizeStoryId(story.id) || buildHuId(sequence++);
    while (used.has(candidate)) {
      candidate = buildHuId(sequence++);
    }
    used.add(candidate);
    return { ...story, id: candidate };
  });
};
